import * as XLSX from 'xlsx';
import { downloadFile } from '../utils/fileHandler';
import {
  $assignment,
  $date,
  $myCallsign,
  exportLogbookContext,
} from '@/stores';
import { closeDialog } from '@/stores/dialog';
import { logs } from '@/stores/logbook';
import { formatTime } from '@/utils/dataUtils';

export function ExportDialog() {
  const exportJson = async () => {
    const data = exportLogbookContext();
    const jsonString = JSON.stringify(data, null, 2);
    downloadFile(jsonString, `logbook_${$date.get() || 'export'}.json`);
  };

  const exportCsv = async () => {
    const allLogs = logs.withGeneratedLogs();
    const csvRows: string[] = [];

    // CSV Header
    csvRows.push('Frequency,Call Sign,Time,Description,Number');

    // Track message numbers
    let messageNumber = 0;

    // Process each log entry
    allLogs.forEach(log => {
      if (log.type === 'message') {
        const msgEntry = log.entries[0]!;
        messageNumber++;

        const frequency = msgEntry.frequency || '-';
        const callSign = msgEntry.callSign
          ? `${msgEntry.callSign.from} -> ${msgEntry.callSign.to}`
          : '-';
        const time = formatTime(msgEntry.time);
        const description = (msgEntry.description || '').replace(/"/g, '""');

        csvRows.push(
          `${frequency},${callSign},${time},"${description}",${messageNumber}`,
        );
      } else if (log.type === 'radioShift') {
        const shiftEntry = log.entries[0]!;

        const time = formatTime(shiftEntry.time);
        const description = (shiftEntry.notes || '').replace(/"/g, '""');

        csvRows.push(`-,-,${time},"${description}",-`);
      } else if (log.type === 'newDay') {
        const { day } = log;
        csvRows.push(`-,-,-,${day},-`);
      }
    });

    const csvContent = csvRows.join('\n');
    downloadFile(csvContent, `logbook_${$date.get() || 'export'}.csv`);
  };

  const exportExcel = async () => {
    // TODO: Mayhaps make look prettier
    const allLogs = logs.withGeneratedLogs();

    // Create workbook
    const workbook = XLSX.utils.book_new();

    // Create array of arrays for the worksheet (to have more control)
    const wsData: any[][] = [];

    // Add metadata header row
    wsData.push([
      `Assignment: ${$assignment.get() || 'N/A'}`,
      `Date: ${$date.get() || 'N/A'}`,
      `Callsign: ${$myCallsign.get() || 'N/A'}`,
      '',
      '',
    ]);

    // Add empty row for spacing
    wsData.push(['', '', '', '', '']);

    // Add column headers
    wsData.push(['Frequency', 'Call Sign', 'Time', 'Description', 'Number']);

    // Track message numbers
    let messageNumber = 0;

    // Process each log entry
    allLogs.forEach(log => {
      if (log.type === 'message') {
        const msgEntry = log.entries[0]!;
        messageNumber++;

        wsData.push([
          msgEntry.frequency || '-',
          msgEntry.callSign
            ? `${msgEntry.callSign.from} -> ${msgEntry.callSign.to}`
            : '-',
          formatTime(msgEntry.time),
          msgEntry.description || '',
          messageNumber,
        ]);
      } else if (log.type === 'radioShift') {
        const shiftEntry = log.entries[0]!;

        wsData.push([
          '-',
          '-',
          formatTime(shiftEntry.time),
          shiftEntry.notes || '',
          '-',
        ]);
      } else if (log.type === 'newDay') {
        const { day } = log;
        wsData.push(['-', '-', '-', day, '-']);
      }
    });

    // Create worksheet from array of arrays
    const worksheet = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Logbook');

    // Set column widths for better readability
    worksheet['!cols'] = [
      { wch: 12 }, // Frequency
      { wch: 15 }, // Call Sign
      { wch: 8 }, // Time
      { wch: 50 }, // Description
      { wch: 8 }, // Number
    ];

    // Generate binary Excel file
    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    // Download the file
    downloadFile(
      new Uint8Array(await blob.arrayBuffer()),
      `logbook_${$date.get() || 'export'}.xlsx`,
    );
  };

  const exportPdf = async () => {
    window.print(); // page already formatted to look good when printed, so just trigger print dialog
  };

  return (
    <div className="stop-scrolling fixed inset-0 z-50 flex items-center justify-center bg-black/50 print:hidden">
      <div
        className="w-96 rounded-lg bg-white shadow-lg"
        onClick={e => e.stopPropagation()}
      >
        <div className="border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-medium text-gray-900">Export Logs</h2>
        </div>
        <div className="px-6 py-4">
          <p className="mb-4 text-gray-700">Choose your export format:</p>
          <div className="space-y-3">
            <button
              onClick={exportJson}
              className="w-full cursor-pointer rounded-md bg-blue-600 px-4 py-2 font-medium text-white transition-colors hover:bg-blue-700"
            >
              Export as JSON
            </button>
            <button
              onClick={exportCsv}
              className="w-full cursor-pointer rounded-md bg-green-600 px-4 py-2 font-medium text-white transition-colors hover:bg-green-700"
            >
              Export as CSV
            </button>
            <button
              onClick={exportExcel}
              className="w-full cursor-pointer rounded-md bg-emerald-600 px-4 py-2 font-medium text-white transition-colors hover:bg-emerald-700"
            >
              Export as Excel
            </button>
            <button
              onClick={exportPdf}
              className="w-full cursor-pointer rounded-md bg-rose-600 px-4 py-2 font-medium text-white transition-colors hover:bg-rose-700"
            >
              Export as PDF
            </button>
          </div>
        </div>
        <div className="flex justify-end border-t border-gray-200 px-6 py-4">
          <button
            onClick={closeDialog}
            className="cursor-pointer rounded-md border border-gray-300 px-4 py-2 font-medium text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
